import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportDropdownButton from "Common/Components/Buttons/ExportDropdownButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import Chip from "components/Chips/Chip";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportProductCSV,
  exportProductTallyCSV,
  fetchProducts,
  updateStatusInProduct,
} from "slices/Masters/product/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePermission from "hooks/usePermission";
import { TruncatedTextWithTooltip } from "Common/Components/TruncatedTextWithTooltip";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import DOMPurify from "dompurify";
import { Tooltip } from "@mui/material";

const Products: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tableInstance, setTableInstance] = useState<any>();
  const [activeTab, setActiveTab] = useState<"products" | "services">(
    "products",
  );
  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState<any[]>([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);
  // const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);

  const tableState = usePreservedTableState({
    key: TableKeys.MASTER.PRODUCT,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;
  const tabParam = searchParams.get("tab") as "products" | "services" | null;

  // Use permission hook
  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.product,
  );

  // Redux selector for products and services state
  const selectProducts = createSelector(
    (state: any) => state.Product,
    (product) => ({
      list: product.list,
      rowCount: product.rowCount,
      loading: product.loading,
      error: product.error,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(selectProducts);

  // Fetch Produdct list
  useEffect(() => {
    dispatch(
      fetchProducts({
        page: pageIndex + 1,
        pageSize,
        sort: sorting?.[0]?.id || "",
        order: sorting?.[0]?.desc ? "desc" : "asc",
        search,
        filters: filterValue,
      }),
    );
  }, [dispatch, activeTab, pageIndex, pageSize, sorting, search, filterValue]);

  useEffect(() => {
    setData(list);
  }, [list]);

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Sync tab from URL
  useEffect(() => {
    if (tabParam && (tabParam === "products" || tabParam === "services")) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Status toggle handler
  const handleStatusToggle = async (item: any) => {
    try {
      const updatedStatus = !item.is_active;
      const payload = { is_active: updatedStatus };
      setIsStatusUpdateLoading(true);
      await dispatch(
        updateStatusInProduct({ id: item.product_id, data: item }),
      );

      const typeLabel = activeTab === "products" ? "Product" : "Service";
      toast.success(
        `${typeLabel} ${
          updatedStatus ? "activated" : "deactivated"
        } successfully`,
      );

      // Refresh list
      dispatch(
        fetchProducts({
          page: pageIndex + 1,
          pageSize,
          sort: sorting?.[0]?.id || "",
          order: sorting?.[0]?.desc ? "desc" : "asc",
          search,
          filters: filterValue,
        }),
      );
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const DyanmicAction = canView || canUpdate
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const menuItems = [
              ...(canView
                ? [
                    {
                      icon: VisibilityIcon,
                      label: "View",
                      permission: canView,
                      onClick: () => {
                        navigate(
                          `/master/product/view/${row.original?.product_id}`,
                        );
                      },
                      color: "primary" as const,
                    },
                  ]
                : []),
              ...(canUpdate
                ? [
                    {
                      icon: EditIcon,
                      label: "Edit",
                      permission: canUpdate,
                      onClick: () => {
                        navigate(
                          `/master/edit-product/${row.original?.product_id}`,
                        );
                      },
                      color: "primary" as const,
                    },
                  ]
                : []),
            ];
            return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
          },
        },
      ]
    : [];

  // Column definitions (Products)
  const columns = [
    {
      header: "Sr. No.",
      accessorKey: "sr_no",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Product Name",
      accessorKey: "product_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "product_name",
      )}`,
      cell: ({ row }: { row: any }) => (
        <TruncatedTextWithTooltip
          text={row.original.product_name}
          maxLines={1}
          minWidth="400px"
          maxWidth="400px"
        />
      ),
    },
    {
      header: "Product SKU",
      accessorKey: "product_sku",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "product_sku",
      )}`,
      cell: ({ row }: { row: any }) => row.original?.product_sku,
    },
    {
      header: "Types",
      accessorKey: "product_types__type__type_name",
      enableColumnFilter: false,
      enableSorting: false,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "product_types__type__type_name",
      )}`,
      cell: ({ row }: { row: any }) => {
        const types = row.original?.product_types;
        if (types.length == 0) return "-";

        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              minWidth: "150px",
            }}
          >
            {types.map((data: any, index: number) => (
              <Chip>{data?.type?.type_name}</Chip>
            ))}
          </div>
        );
      },
    },
    {
      header: "Category",
      accessorKey: "category__category_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "category__category_name",
      )}`,
      cell: ({ row }: { row: any }) => row.original?.category?.category_name,
    },
    {
      header: "Sub-Category",
      accessorKey: "sub_category__sub_category_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "sub_category__sub_category_name",
      )}`,
      cell: ({ row }: { row: any }) =>
        row.original?.sub_category?.sub_category_name,
    },
    {
      header: "Make",
      accessorKey: "make__make_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "make__make_name",
      )}`,
      cell: ({ row }: { row: any }) => row.original?.make?.make_name ?? "",
    },
    {
      header: "Model",
      accessorKey: "model__model_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "model__model_name",
      )}`,
      cell: ({ row }: { row: any }) => row.original?.model?.model_name ?? "",
    },
    {
      header: "Unit",
      accessorKey: "unit__unit_name",
      enableColumnFilter: true,
      enableSorting: false,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "unit__unit_name",
      )}`,
      cell: ({ row }: { row: any }) => row.original?.unit?.unit_name ?? "",
    },
    {
      header: "Unit Price",
      accessorKey: "unit_price",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }: { row: any }) =>
        row.original.unit_price != null && row.original.unit_price !== ""
          ? `₹${formatIndianAmount(row.original.unit_price)}`
          : "₹0.00",
    },
    {
      header: "HSN Code",
      accessorKey: "hsn__hsn_code",
      enableColumnFilter: true,
      enableSorting: false,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "hsn__hsn_code",
      )}`,
      cell: ({ row }: { row: any }) => row.original?.hsn?.hsn_code,
    },
    {
      header: "GST %",
      accessorKey: "hsn__gst__gst_percentage",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
        "hsn__gst__gst_percentage",
      )}`,
      cell: ({ row }: { row: any }) => row.original?.hsn?.gst?.gst_percentage,
    },
    {
      header: "Description",
      accessorKey: "prod_description",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const rawHtml = row.original?.prod_description ?? "";
        if (!rawHtml || !rawHtml.trim()) return <span className="text-muted">-</span>;
        const safeHtml = DOMPurify.sanitize(rawHtml);
        return (
          <Tooltip
            arrow
            placement="top"
            title={
              <div
                className="product-description-tooltip"
                style={{
                  maxWidth: "400px",
                  maxHeight: "320px",
                  overflow: "auto",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            }
            componentsProps={{
              tooltip: {
                sx: { maxWidth: 500, p: 1.5, "& img": { maxWidth: "100%", height: "auto", maxHeight: 200 } },
              },
            }}
          >
            <div
              className="product-description-cell"
              style={{
                minWidth: "180px",
                maxWidth: "280px",
                maxHeight: "80px",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                wordBreak: "break-word",
                whiteSpace: "normal",
                cursor: "pointer",
                fontSize: "13px",
              }}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </Tooltip>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "is_active",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const pkg = row.original;
        return (
          <div
            style={{ fontSize: "1.5rem", cursor: "pointer" }}
            onClick={() => handleStatusToggle(pkg)}
          >
            <i
              className={`bi ${
                pkg.is_active
                  ? "bi-toggle-on text-success"
                  : "bi-toggle-off text-danger"
              }`}
            />
          </div>
        );
      },
    },

    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => (
        <NameWithDate
          name={row.original.created_by_user?.username}
          date={row.original.created_at}
        />
      ),
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => (
        <NameWithDate
          name={row.original.updated_by_user?.username}
          date={row.original.updated_at}
        />
      ),
    },
    ...DyanmicAction,
  ];

  return (
    <ListingPageContainer>
      <style>{`
        .product-description-cell img {
          max-width: 100%;
          max-height: 56px;
          height: auto;
          object-fit: contain;
          vertical-align: middle;
        }
        .product-description-tooltip img {
          max-width: 100%;
          max-height: 180px;
          height: auto;
          object-fit: contain;
        }
      `}</style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Product</h6>
                </Col>
              </Row>

              <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                {/* Search Box */}
                <SearchBox
                  value={searchInput}
                  onChange={(e: any) => setSearchInput(e.target.value)}
                  placeholder="Search..."
                />

                {/* Column Visibility + Actions */}
                <Col xs={12} md={12} lg={8} xl={6} className="">
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                    {/* Left side: Column Visibility */}
                    <div className="">
                      {tableInstance && (
                        <ColumnVisibilityDropdown
                          columns={tableInstance.getAllLeafColumns()}
                          excludedColumns={["action", "sr_no"]}
                        />
                      )}
                    </div>

                    <div className="hstack gap-2">
                      {canView && (
                        <ExportDropdownButton
                          options={[
                            {
                              label: "Export CSV",
                              onExport: async () =>
                                exportProductCSV({
                                  search,
                                  filters: filterValue,
                                }),
                            },
                            {
                              label: "Export for Tally",
                              onExport: async () =>
                                exportProductTallyCSV({
                                  search,
                                  filters: filterValue,
                                }),
                            },
                          ]}
                        />
                      )}
                    </div>

                    <div>
                      {canCreate && (
                        <ActionButton
                          label={"Add Products"}
                          icon="bi bi-plus-circle"
                          navigateLink={`/master/add-product`}
                          variant="primary"
                        />
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <MasterTableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={(updaterOrValue) => {
                    setSorting(
                      typeof updaterOrValue === "function"
                        ? updaterOrValue(sorting)
                        : updaterOrValue,
                    );
                  }}
                  isBordered={false}
                  isPagination={true}
                  manualSorting={true}
                  colValue={setFilterValue}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Products;
